import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Check, ChevronDown, ChevronUp, Eye, User, Target, Palette, FileText, Heart, Wand2 } from 'lucide-react';

// Map source IDs to icons and labels
const sourceConfig = {
  vision: { icon: Eye, label: 'Vision', color: 'text-purple-400' },
  profile: { icon: User, label: 'Profil', color: 'text-blue-400' },
  audit: { icon: Target, label: 'Audit', color: 'text-green-400' },
  artistic: { icon: Palette, label: 'Direction artistique', color: 'text-pink-400' },
  content: { icon: FileText, label: 'Stratégie contenu', color: 'text-orange-400' },
  expectations: { icon: Heart, label: 'Objectifs', color: 'text-red-400' },
  identity_card: { icon: Wand2, label: 'Identité', color: 'text-cyan-400' },
  glow_up: { icon: Sparkles, label: 'Glow Up', color: 'text-yellow-400' }
};

/**
 * AutofillBanner - Premium display when user data has been pre-loaded
 * Shows a personalized message and visual checkmarks for data sources
 */
const AutofillBanner = ({ 
  hasData, 
  sources = [], 
  sourcesDisplay = [],
  message = null,
  showDetails = false,
  className = ""
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  if (!hasData || sources.length === 0) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-gradient-to-r from-[#D4AF37]/10 via-[#1C1C1C] to-[#0A0A0A] border border-[#D4AF37]/30 rounded-xl overflow-hidden mb-6 ${className}`}
      data-testid="autofill-banner"
    >
      {/* Main Content */}
      <div className="p-5">
        <div className="flex items-start gap-4">
          {/* Animated Icon */}
          <motion.div 
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#E5C585] flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#D4AF37]/20"
          >
            <Sparkles className="w-6 h-6 text-black" />
          </motion.div>

          {/* Message */}
          <div className="flex-1 pt-1">
            <p className="text-white text-base font-medium leading-relaxed">
              {message || "Tes données ont été analysées pour cette génération."}
            </p>
          </div>

          {/* Toggle for details */}
          {showDetails && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-2 text-[#D4AF37] hover:text-[#E5C585] transition-colors rounded-lg hover:bg-[#D4AF37]/10"
              aria-label={isExpanded ? "Masquer les détails" : "Voir les sources"}
            >
              {isExpanded ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </button>
          )}
        </div>

        {/* Sources Chips - Always visible */}
        <div className="flex flex-wrap gap-2 mt-4 ml-16">
          {sources.map((sourceId) => {
            const config = sourceConfig[sourceId] || { icon: Check, label: sourceId, color: 'text-gray-400' };
            const IconComponent = config.icon;
            
            return (
              <motion.div
                key={sourceId}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center gap-2 px-3 py-1.5 bg-[#0A0A0A] border border-[#27272A] rounded-full"
              >
                <Check className="w-3.5 h-3.5 text-[#D4AF37]" />
                <IconComponent className={`w-3.5 h-3.5 ${config.color}`} />
                <span className="text-xs text-white font-medium">{config.label}</span>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Expandable Details */}
      <AnimatePresence>
        {showDetails && isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-[#27272A] bg-[#0A0A0A]/50"
          >
            <div className="p-5">
              <p className="text-[#71717A] text-xs uppercase tracking-wider mb-4">
                Données utilisées pour cette génération
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {sources.map((sourceId, idx) => {
                  const config = sourceConfig[sourceId] || { icon: Check, label: sourceId, color: 'text-gray-400' };
                  const IconComponent = config.icon;
                  const displayName = sourcesDisplay[idx] || config.label;
                  
                  return (
                    <div 
                      key={sourceId}
                      className="flex items-center gap-3 p-3 bg-[#111] border border-[#1C1C1C] rounded-lg"
                    >
                      <div className={`w-8 h-8 rounded-full bg-[#1C1C1C] flex items-center justify-center`}>
                        <IconComponent className={`w-4 h-4 ${config.color}`} />
                      </div>
                      <div>
                        <p className="text-white text-sm font-medium">{displayName}</p>
                        <p className="text-[#52525B] text-xs flex items-center gap-1">
                          <Check className="w-3 h-3 text-green-500" />
                          Intégré
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer hint */}
      <div className="px-5 py-3 bg-[#0A0A0A] border-t border-[#1C1C1C]">
        <p className="text-[#52525B] text-xs text-center">
          Tu peux modifier ces informations à tout moment
        </p>
      </div>
    </motion.div>
  );
};

/**
 * Compact badge version for smaller spaces (e.g., AI Assistant header)
 */
export const AutofillBadge = ({ hasData, sources = [] }) => {
  if (!hasData || sources.length === 0) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#D4AF37]/20 to-transparent border border-[#D4AF37]/30 rounded-full"
      data-testid="autofill-badge"
    >
      <Sparkles className="w-4 h-4 text-[#D4AF37]" />
      <span className="text-sm text-[#E5C585] font-medium">
        Personnalisé pour toi
      </span>
      <div className="flex -space-x-1">
        {sources.slice(0, 3).map((sourceId) => {
          const config = sourceConfig[sourceId];
          if (!config) return null;
          const IconComponent = config.icon;
          return (
            <div 
              key={sourceId}
              className="w-5 h-5 rounded-full bg-[#1C1C1C] border border-[#27272A] flex items-center justify-center"
            >
              <IconComponent className={`w-2.5 h-2.5 ${config.color}`} />
            </div>
          );
        })}
      </div>
    </motion.div>
  );
};

export default AutofillBanner;
